import { useRef } from 'react';
import AsyncSelect from 'react-select/async';
import service from '@Service';

export interface FishOption {
  value: string;
  label: string;
}

const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 25;
const DEBOUNCE_MS = 250;

// Map a Meilisearch `fish` index hit (flat MemberDocument shape) to a select option.
const hitToOption = (hit: any): FishOption => {
  const fullName = `${hit?.fullName ?? ''}`.trim();
  const contact = hit?.email || hit?.phoneNumber || hit?.id;
  return {
    value: hit.id,
    label: fullName ? `${fullName} (${contact})` : String(contact),
  };
};

interface FishSearchSelectProps {
  value: FishOption | null;
  onChange: (option: FishOption | null) => void;
  placeholder?: string;
  className?: string;
  isClearable?: boolean;
}

/**
 * Async fish picker backed by the Meilisearch `fish` index (all fish, guests
 * included) via `GET /search/fish`. Drop-in replacement for the legacy
 * database-backed fish autocomplete.
 */
const FishSearchSelect = ({
  value,
  onChange,
  placeholder = 'Search for a member by name, phone, or email…',
  className = 'mt-1',
  isClearable = true,
}: FishSearchSelectProps) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const loadFishOptions = (search: string): Promise<FishOption[]> =>
    new Promise((resolve) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!search || search.length < MIN_QUERY_LENGTH) {
        resolve([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        try {
          const data = await service.searchFish(search, MAX_RESULTS);
          resolve((data?.hits ?? []).map(hitToOption));
        } catch {
          resolve([]);
        }
      }, DEBOUNCE_MS);
    });

  return (
    <AsyncSelect<FishOption>
      cacheOptions
      defaultOptions={false}
      loadOptions={loadFishOptions}
      value={value}
      onChange={(opt) => onChange(opt)}
      placeholder={placeholder}
      isClearable={isClearable}
      className={className}
      classNamePrefix="select"
    />
  );
};

export default FishSearchSelect;
